/**
 * UIManager - Topbar and Control Panels
 * Builds and manages the UI for MIDI connection, audio connection,
 * MIDI activity monitoring, parameter mapping, and performance (scene + preset) editing.
 * All state changes flow through StateManager; no direct manager DOM manipulation.
 */

import {
  deriveSceneControlDefsForSceneTab,
  deriveGlobalEnvControlDefs,
  getGlobalEnvKeyUsage,
  getGlobalEnvSceneMeta,
  getGlobalEnvValueSource,
  getMergedEffectiveEnv,
  GLOBAL_ENV_BINDING_TARGETS,
  GLOBAL_ENV_KEYS,
  setGlobalEnvControlBinding,
  setUnifiedControlBinding,
  setUnifiedControlValue,
  summarizeGlobalEnvUsageForScene,
} from '../performance/schema.js';
import { listSceneIds, getSceneMeta } from '../scenes/registry.js';
import { BPM_BUILTIN_SIGNAL_IDS, sortSignalNamesForUi } from '../timing/bpmBuiltinSignals.js';

const NOTE_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
function midiNoteName(midi) {
  return NOTE_NAMES[midi % 12] + (Math.floor(midi / 12) - 1);
}

/** Escape for use in HTML attribute values */
function escapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/** Preset list / HUD label; stable id is `key`. */
function presetDisplayLabel(key, preset) {
  const d = preset?.displayName;
  if (d != null && String(d).trim()) return String(d).trim();
  return key;
}

function perfControlDomId(pk) {
  return `ui-perf-c-${String(pk).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function perfEnvControlDomId(pk) {
  return `ui-perf-e-${String(pk).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function formatGlobalEnvUsageLabel(usage) {
  switch (usage.kind) {
    case 'post':
      return 'Post-process (bloom)';
    case 'scene':
      return 'Affects this scene';
    case 'unused':
    default:
      return 'No effect in this scene';
  }
}

function formatGlobalEnvSourceLabel(source) {
  switch (source) {
    case 'preset':
      return 'Value from: this preset';
    case 'shared':
      return 'Value from: performance (shared)';
    case 'default':
    default:
      return 'Value from: app default';
  }
}

function formatParamLabel(pk) {
  return String(pk)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatControlSourceLabel(source) {
  const raw = String(source || '').trim().toLowerCase();
  if (raw === 'gui') return 'GUI';
  if (raw === 'uniform') return 'Uniform';
  if (raw === 'prop') return 'Property';
  if (raw === 'imported') return 'Imported';
  if (raw === 'discovered') return 'Discovered';
  return 'Future';
}

function hexToRgb(hex) {
  const s = String(hex || '').trim().replace('#', '');
  if (!s) return [102, 170, 255];
  const x = s.length === 3 ? s.split('').map((c) => c + c).join('') : s.padEnd(6, '0').slice(0, 6);
  const n = parseInt(x, 16);
  if (!Number.isFinite(n)) return [102, 170, 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function hexToHsl(hex) {
  return rgbToHsl(...hexToRgb(hex));
}

function hslToRgb(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return [
    Math.round((rp + m) * 255),
    Math.round((gp + m) * 255),
    Math.round((bp + m) * 255),
  ];
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function hslToHex(h, s, l) {
  return rgbToHex(...hslToRgb(h, s, l));
}

/**
 * @param {object} meta scene meta from registry
 * @param {string} pk param key
 * @param {unknown} defVal from defaultParams
 */
function resolveParamSpec(meta, pk, defVal) {
  const ps = meta.paramSpecs?.[pk];
  if (ps?.type === 'hue') {
    return { type: 'hue', label: ps.label || formatParamLabel(pk) };
  }
  if (typeof defVal === 'number') {
    const min = ps?.min ?? (defVal >= 0 && defVal <= 1 ? 0 : 0);
    const max =
      ps?.max ?? (defVal >= 0 && defVal <= 1 ? 1 : Math.max(128, Math.abs(defVal) * 4));
    const step = ps?.step ?? (Number.isInteger(defVal) ? 1 : null);
    const format =
      ps?.format ??
      (max <= 1 && min >= 0
        ? (v) => v.toFixed(2)
        : (v) => String(Math.round(v)));
    return {
      type: 'number',
      min,
      max,
      step,
      label: ps?.label || formatParamLabel(pk),
      format,
    };
  }
  if (
    typeof defVal === 'string' &&
    (ps?.type === 'hue' || /^#[0-9a-fA-F]{3,8}$/.test(String(defVal).trim()))
  ) {
    return { type: 'hue', label: ps?.label || formatParamLabel(pk) };
  }
  return { type: 'text', label: ps?.label || formatParamLabel(pk) };
}

/** Next `control_n` not already used in CC or note mappings. */
function nextControlName(state) {
  const used = new Set();
  for (const m of [
    ...Object.values(state.get('midiCCMappings') || {}),
    ...Object.values(state.get('midiNoteMappings') || {}),
  ]) {
    const raw = m?.name && String(m.name).trim();
    const match = raw && /^control_(\d+)$/.exec(raw);
    if (match) used.add(Number(match[1]));
  }
  let n = 1;
  while (used.has(n)) n++;
  return `control_${n}`;
}

// ── SVG icons ───────────────────────────────────────────────
const svg = (w, h, d) =>
  `<svg class="icon" width="${w}" height="${h}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">${d}</svg>`;

const iconPlus    = (s) => svg(s, s, '<path d="M5 12h14M12 5v14"/>');
const iconX       = (s) => svg(s, s, '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>');
const iconRefresh = (s) => svg(s, s, '<path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>');
const iconMapping = (s) => svg(s, s, '<path d="M9 18l-6-6 6-6M15 6l6 6-6 6"/>');
const iconScenes  = (s) => svg(s, s, '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>');
const iconPencil  = (s) =>
  svg(
    s,
    s,
    '<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="m15 5 4 4"/>'
  );
const iconChevronRight = (s) =>
  svg(s, s, '<path d="m9 18 6-6-6-6"/>');

export class UIManager {
  constructor({
    state,
    midiManager,
    audioManager,
    midiEventHandler,
    getSceneMidiTargets,
    app,
  }) {
    this.state            = state;
    this.midiManager      = midiManager;
    this.audioManager     = audioManager;
    this.midiEventHandler = midiEventHandler;
    /** @type {import('../core/App.js').App | null} */
    this.app = app || null;
    this._getSceneMidiTargets =
      typeof getSceneMidiTargets === 'function' ? getSceneMidiTargets : () => [];

    this._openPanel    = null;
    /** @type {string | null} preset id while inline display-name edit is open */
    this._renamingPresetKey = null;
    /** Performance panel: narrow hub (presets) vs drill-in preset editor */
    this._perfUiView = 'hub';
    /** Controls tab: skip full perf panel rebuild while dragging so the track isn’t destroyed mid-gesture */
    this._tuneParamDragging = false;
    this._performanceDirtyDuringTuneDrag = false;
    this._endTuneParamDrag = () => {
      this._tuneParamDragging = false;
      this._performanceDirtyDuringTuneDrag = false;
      // Intentionally no _renderPerformancePanel here — the slider DOM already matches state,
      // and a rebuild can snap thumbs (float / hex rounding) after a smooth drag.
    };
    this._learnCb      = null;
    this._learnType    = null; // 'cc' | 'note'

    this._activityMessages = [];
    this._filterClock      = true;
    this._monitorPaused    = false;
    this._maxMessages      = 100;
    this._msgStats         = { cc: 0, notes: 0, pitchBend: 0, system: 0 };
    this._lastActivityTime = null;
    this._msgsThisSec      = 0;
    this._msgRateInterval  = setInterval(() => this._updateMsgRate(), 1000);

    this._build();
    this._bindEvents();
    this._bindState();
    this.midiManager.onDevicesChange = () => this._populateMIDISelect();
  }

  getSceneMidiTargets() {
    try {
      return this._getSceneMidiTargets() || [];
    } catch (e) {
      console.error('getSceneMidiTargets:', e);
      return [];
    }
  }

  /** Rig signal names + built-in bpm_* tempo divisions (Scene / Preset binding dropdown). */
  _collectRigSignalNames() {
    const perf = this.state.get('performance');
    const keys = Object.keys(perf?.rig?.signals || {});
    return sortSignalNamesForUi([...BPM_BUILTIN_SIGNAL_IDS, ...keys]);
  }

  _controlNameSelectOptions(selected) {
    const names = this._collectRigSignalNames();
    const sel = selected == null || selected === '' ? '' : String(selected);
    const head = `<option value=""${sel === '' ? ' selected' : ''}>—</option>`;
    if (!names.length) {
      return `${head}<option value="" disabled>No rig signals — check Performance / Mapping</option>`;
    }
    return (
      head +
      names
        .map((n) => `<option value="${escapeAttr(n)}"${n === sel ? ' selected' : ''}>${escapeAttr(n)}</option>`)
        .join('')
    );
  }

  _ensureSceneBindingKeys() {
    const targets = this.getSceneMidiTargets();
    const cur = { ...(this.state.get('sceneBindings') || {}) };
    let changed = false;
    for (const t of targets) {
      if (cur[t.key] === undefined) {
        cur[t.key] = '';
        changed = true;
      }
    }
    for (const k of Object.keys(cur)) {
      if (!targets.some((t) => t.key === k)) {
        delete cur[k];
        changed = true;
      }
    }
    if (changed) this.state.set('sceneBindings', cur);
  }

  _pruneSceneBindings() {
    const valid = new Set(this._collectRigSignalNames());
    const cur = { ...(this.state.get('sceneBindings') || {}) };
    let changed = false;
    for (const [paramKey, controlName] of Object.entries(cur)) {
      const cn = String(controlName).trim();
      if (cn && !valid.has(cn)) {
        cur[paramKey] = '';
        changed = true;
      }
    }
    if (changed) this.state.set('sceneBindings', cur);
  }

  _stripControlValue(name) {
    const n = String(name).trim();
    if (!n) return;
    const cv = { ...(this.state.get('controlValues') || {}) };
    if (cv[n] === undefined) return;
    delete cv[n];
    this.state.set('controlValues', cv);
  }

  // ─────────────────────────────────────────────────
  // Build
  // ─────────────────────────────────────────────────

  _build() {
    const old = document.getElementById('status-bar');
    if (old) old.remove();

    const root = document.createElement('div');
    root.id = 'ui-root';
    root.innerHTML =
      this._topbarHTML() +
      this._panelConnectHTML() +
      this._panelMIDIActivityHTML() +
      this._panelMappingHTML() +
      this._panelPerformanceHTML();

    document.getElementById('app').appendChild(root);
    this._fileInput = null;

    this._renderCCMappings();
    this._renderNoteMappings();
    this._renderSceneBindings();
    this._renderPerformancePanel();
    this._applyUiMode();
  }

  _topbarHTML() {
    return `
<div class="topbar">
  <div class="topbar-left">
    <div class="tb-mode" role="group" aria-label="UI mode">
      <button type="button" class="tb-chip" id="ui-mode-perform" data-ui-mode="perform">Perform</button>
      <button type="button" class="tb-chip active" id="ui-mode-edit" data-ui-mode="edit">Edit</button>
    </div>
    <button class="tb-btn tb-edit-only" id="ui-btn-connect">
      ${iconPlus(11)} Connect
    </button>
    <div class="tb-btn tb-edit-only" style="pointer-events:none; gap:6px;">
      <span class="status-dot" id="ui-midi-dot"></span>
      <span id="ui-midi-label">No MIDI</span>
    </div>
    <div class="tb-btn tb-edit-only" style="pointer-events:none; gap:6px;">
      <span class="status-dot" id="ui-audio-dot"></span>
      <span id="ui-audio-label">No Audio</span>
    </div>
    <button class="tb-btn tb-edit-only" id="ui-btn-midi-activity">
      <div class="midi-activity-bars">
        <div class="midi-bar"></div><div class="midi-bar"></div>
        <div class="midi-bar"></div><div class="midi-bar"></div>
        <div class="midi-bar"></div>
      </div>
      MIDI Activity
    </button>
  </div>
  <div class="topbar-mid" id="ui-perform-hud">
    <span class="tb-hud-label">Preset</span>
    <span id="ui-hud-preset" class="tb-hud-value">—</span>
    <span class="tb-hud-sep">·</span>
    <span class="tb-hud-label">Scene</span>
    <span id="ui-hud-scene" class="tb-hud-value muted">—</span>
    <span class="tb-hud-sep">·</span>
    <span class="tb-hud-label">PC</span>
    <span id="ui-hud-pc" class="tb-hud-value">0</span>
  </div>
  <div class="topbar-right">
    <span id="fps-display" class="tb-value" style="color:var(--muted);"></span>
    <span id="bpm-display" class="tb-value">120 BPM</span>
    <button class="tb-btn tb-edit-only" id="ui-btn-performance">
      ${iconScenes(11)} Performance
    </button>
    <button class="tb-btn tb-edit-only" id="ui-btn-mapping">
      ${iconMapping(11)} Mapping
    </button>
  </div>
</div>`;
  }

  _panelPerformanceHTML() {
    return `
<div class="panel-wrapper right" id="ui-panel-performance">
  <div class="panel panel--performance-max">
    <div id="ui-perf-hub-view">
      <div class="panel-header">
        <div class="panel-title"><span class="panel-title-dot" style="background:var(--accent2);"></span> Performance</div>
      </div>
      <div class="section perf-section perf-section--hub">
        <div class="perf-col-island">
          <div class="perf-card perf-card--rig">
            <div class="field">
              <div class="field-label">Performance name</div>
              <input type="text" class="map-field" id="ui-perf-name" placeholder="Thursday night" />
            </div>
            <div class="section-label perf-presets-title">Presets</div>
            <p class="perf-presets-hint">Tap a preset to edit setup and controls.</p>
            <div id="ui-perf-preset-list" class="perf-preset-list"></div>
            <button type="button" class="btn btn-ghost perf-add-preset" id="ui-perf-add-preset">+ Add preset</button>
          </div>
          <div class="perf-card perf-card--snapshot">
            <div class="perf-snapshot-label">Snapshot</div>
            <div class="btn-row perf-snapshot-actions">
              <button type="button" class="btn btn-primary" style="flex:1;" id="ui-perf-export">Export JSON</button>
              <button type="button" class="btn btn-ghost" style="flex:1;" id="ui-perf-import">Import…</button>
            </div>
            <input type="file" id="ui-perf-file" accept="application/json,.json" style="display:none;" />
          </div>
        </div>
      </div>
    </div>
    <div id="ui-perf-detail-view" class="perf-detail-view" hidden>
      <div class="perf-detail-header">
        <button type="button" class="btn btn-ghost perf-detail-back" id="ui-perf-back">← Back</button>
        <div class="perf-detail-title-row">
          <div class="perf-detail-title-slot" id="ui-perf-detail-title-slot"></div>
          <button type="button" class="btn btn-ghost btn-icon perf-detail-rename" id="ui-perf-detail-rename" title="Rename preset" aria-label="Rename preset">${iconPencil(14)}</button>
        </div>
      </div>
      <div class="tabs perf-detail-tabs">
        <div class="tab active" data-tab-target="ui-perf-tab-controls" data-tab-group="perf-detail-tabs">Controls</div>
        <div class="tab" data-tab-target="ui-perf-tab-env" data-tab-group="perf-detail-tabs">Environment</div>
        <div class="tab" data-tab-target="ui-perf-tab-setup" data-tab-group="perf-detail-tabs">Settings</div>
      </div>
      <div class="section perf-section perf-detail-section">
        <div id="ui-perf-tab-controls" class="tab-content active perf-detail-tab-panel" data-group="perf-detail-tabs">
          <div id="ui-perf-controls"></div>
        </div>
        <div id="ui-perf-tab-env" class="tab-content perf-detail-tab-panel" data-group="perf-detail-tabs">
          <div id="ui-perf-env-panel"></div>
        </div>
        <div id="ui-perf-tab-setup" class="tab-content perf-detail-tab-panel" data-group="perf-detail-tabs">
          <div id="ui-perf-detail-scene"></div>
        </div>
      </div>
    </div>
  </div>
</div>`;
  }

  _panelConnectHTML() {
    return `
<div class="panel-wrapper left" id="ui-panel-connect">
  <div class="panel">
    <div class="panel-header">
      <div class="panel-title"><span class="panel-title-dot"></span> Connect</div>
    </div>
    <div class="section">
      <div class="tabs">
        <div class="tab active" data-tab-target="ui-midi-tab" data-tab-group="connect-tabs">MIDI</div>
        <div class="tab" data-tab-target="ui-audio-tab" data-tab-group="connect-tabs">Audio</div>
      </div>

      <!-- MIDI Tab -->
      <div id="ui-midi-tab" class="tab-content active" data-group="connect-tabs">
        <div class="status-card" style="margin-bottom:12px;" id="ui-midi-status-card">
          <div class="status-card-icon err" id="ui-midi-card-icon">🎹</div>
          <div class="status-card-body">
            <div class="status-card-title">MIDI Device</div>
            <div class="status-card-sub" id="ui-midi-device-sub">No device connected</div>
          </div>
          <span class="status-tag disconnected" id="ui-midi-status-tag">Off</span>
        </div>
        <div class="field">
          <div class="field-label">Device</div>
          <div class="btn-row" style="margin-bottom:0;">
            <select id="ui-midi-select" style="flex:1;"><option value="">No MIDI devices found</option></select>
            <button class="btn btn-ghost btn-icon" id="ui-midi-disconnect-btn" title="Disconnect">${iconX(10)}</button>
          </div>
        </div>
        <div class="btn-row" style="margin-bottom:12px;">
          <button class="btn btn-primary" id="ui-midi-connect-btn" style="flex:1;">
            ${iconPlus(10)} Connect MIDI
          </button>
          <button class="btn btn-ghost btn-icon" id="ui-midi-refresh-btn" title="Refresh">${iconRefresh(10)}</button>
        </div>
        <div class="divider" style="margin-bottom:12px;"></div>
        <div class="toggle-row">
          <div class="toggle-info">
            <div class="toggle-title">MIDI Stop Pauses Animation</div>
            <div class="toggle-desc">Receiving a MIDI stop message pauses the animation loop</div>
          </div>
          <label class="toggle">
            <input type="checkbox" id="ui-midi-stop-toggle">
            <div class="toggle-track"></div>
            <div class="toggle-thumb"></div>
          </label>
        </div>
      </div>

      <!-- Audio Tab -->
      <div id="ui-audio-tab" class="tab-content" data-group="connect-tabs">
        <div class="status-card" style="margin-bottom:12px;" id="ui-audio-status-card">
          <div class="status-card-icon err" id="ui-audio-card-icon">🎙️</div>
          <div class="status-card-body">
            <div class="status-card-title">Audio Interface</div>
            <div class="status-card-sub" id="ui-audio-device-sub">No interfaces available</div>
          </div>
          <span class="status-tag disconnected" id="ui-audio-status-tag">Off</span>
        </div>
        <div class="field">
          <div class="field-label">Interface</div>
          <div class="btn-row" style="margin-bottom:0;">
            <select id="ui-audio-select" style="flex:1;"><option value="">No interfaces available</option></select>
            <button class="btn btn-ghost btn-icon" id="ui-audio-disconnect-btn" title="Disconnect">${iconX(10)}</button>
          </div>
        </div>
        <div class="btn-row" style="margin-bottom:12px;">
          <button class="btn btn-primary" id="ui-audio-connect-btn" style="flex:1;">
            ${iconPlus(10)} Connect Audio
          </button>
          <button class="btn btn-ghost btn-icon" id="ui-audio-refresh-btn" title="Refresh">${iconRefresh(10)}</button>
        </div>
        <div class="divider" style="margin-bottom:12px;"></div>
        <div class="section-label">Sensitivity</div>
        <div class="slider-row" style="margin-bottom:14px;">
          <div class="slider-track" id="ui-sensitivity-track">
            <div class="slider-fill" id="ui-sensitivity-fill" style="width:50%"></div>
            <div class="slider-thumb" id="ui-sensitivity-thumb" style="left:50%"></div>
          </div>
          <div class="slider-value" id="ui-sensitivity-val">1.0</div>
        </div>
        <div class="divider" style="margin-bottom:12px;"></div>
        <div class="section-label">Live Analysis</div>
        <div class="meter-grid">
          <div class="meter">
            <div class="meter-label">Overall</div>
            <div class="meter-value" id="ui-meter-overall">0.00</div>
            <div class="meter-bar-track"><div class="meter-bar-fill" id="ui-meter-overall-bar" style="width:0%"></div></div>
          </div>
          <div class="meter">
            <div class="meter-label">RMS</div>
            <div class="meter-value" id="ui-meter-rms">0.00</div>
            <div class="meter-bar-track"><div class="meter-bar-fill" id="ui-meter-rms-bar" style="width:0%"></div></div>
          </div>
          <div class="meter">
            <div class="meter-label">Peak</div>
            <div class="meter-value" id="ui-meter-peak">0.00</div>
            <div class="meter-bar-track"><div class="meter-bar-fill" id="ui-meter-peak-bar" style="width:0%"></div></div>
          </div>
          <div class="meter">
            <div class="meter-label">Freq</div>
            <div class="meter-value" id="ui-meter-freq">0 Hz</div>
            <div class="meter-bar-track"><div class="meter-bar-fill" id="ui-meter-freq-bar" style="width:0%"></div></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`;
  }

  _panelMIDIActivityHTML() {
    return `
<div class="panel-wrapper left" id="ui-panel-midi-activity">
  <div class="panel">
    <div class="panel-header">
      <div class="panel-title"><span class="panel-title-dot blue"></span> MIDI Activity</div>
      <div style="display:flex; gap:5px;">
        <button class="btn btn-ghost" style="height:24px;padding:0 8px;font-size:10px;" id="ui-activity-clear">Clear</button>
        <button class="btn btn-ghost" style="height:24px;padding:0 8px;font-size:10px;" id="ui-activity-pause">Pause</button>
      </div>
    </div>
    <div class="section">
      <div class="monitor-toolbar" style="margin-bottom:10px;">
        <span class="label">Max:</span>
        <div class="chip-group">
          <div class="chip" data-max="50">50</div>
          <div class="chip active" data-max="100">100</div>
          <div class="chip" data-max="200">200</div>
          <div class="chip" data-max="500">500</div>
        </div>
        <span class="label" style="margin-left:4px;">Filter Clock</span>
        <label class="toggle" style="transform:scale(0.85);transform-origin:left;">
          <input type="checkbox" id="ui-filter-clock" checked>
          <div class="toggle-track"></div>
          <div class="toggle-thumb"></div>
        </label>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
        <span style="font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;">Message Stream</span>
        <span style="font-size:10px;color:var(--muted);" id="ui-activity-count">0 messages</span>
      </div>
      <div class="midi-stream" id="ui-midi-stream">No MIDI activity detected</div>
    </div>
    <div class="section">
      <div class="section-label">Message Types</div>
      <div class="stats-grid">
        <div class="stat-cell"><span class="stat-label">CC</span><span class="stat-value zero" id="ui-stat-cc">0</span></div>
        <div class="stat-cell"><span class="stat-label">Notes</span><span class="stat-value zero" id="ui-stat-notes">0</span></div>
        <div class="stat-cell"><span class="stat-label">Pitch Bend</span><span class="stat-value zero" id="ui-stat-pb">0</span></div>
        <div class="stat-cell"><span class="stat-label">System</span><span class="stat-value zero" id="ui-stat-sys">0</span></div>
      </div>
    </div>
    <div class="section">
      <div class="section-label">Connection Status</div>
      <div class="conn-status-grid">
        <div class="conn-cell">
          <div class="conn-label">Status</div>
          <div class="conn-value danger" id="ui-conn-status">Disconnected</div>
        </div>
        <div class="conn-cell">
          <div class="conn-label">Device</div>
          <div class="conn-value muted" id="ui-conn-device">None</div>
        </div>
        <div class="conn-cell">
          <div class="conn-label">Msg/sec</div>
          <div class="conn-value muted" id="ui-conn-msgrate">0</div>
        </div>
        <div class="conn-cell">
          <div class="conn-label">Last Activity</div>
          <div class="conn-value muted" id="ui-conn-lastact">Never</div>
        </div>
      </div>
    </div>
  </div>
</div>`;
  }

  _panelMappingHTML() {
    return `
<div class="panel-wrapper right" id="ui-panel-mapping">
  <div class="panel">
    <div class="panel-header">
      <div class="panel-title"><span class="panel-title-dot"></span> Mapping</div>
    </div>
    <div class="section">
      <div class="tabs">
        <div class="tab active" data-tab-target="ui-map-cc"   data-tab-group="map-tabs">CC</div>
        <div class="tab"        data-tab-target="ui-map-note" data-tab-group="map-tabs">Notes</div>
        <div class="tab"        data-tab-target="ui-map-audio" data-tab-group="map-tabs">Audio</div>
      </div>

      <!-- CC Tab -->
      <div id="ui-map-cc" class="tab-content active" data-group="map-tabs">
        <div id="ui-cc-list"></div>
        <div id="ui-cc-learn-prompt" style="display:none;">
          <div class="empty-state" style="border-color:var(--accent); margin-bottom:8px;">
            <div class="empty-state-icon">🎛️</div>
            <div class="empty-state-title" style="color:var(--accent);">Move a knob or fader...</div>
            <p style="font-size:10px;color:var(--muted);text-align:left;line-height:1.45;margin:0;">
              Confirm to save a <b style="color:var(--text);">name</b> for this CC. Wire that name to rig signals under <b>Performance</b>.
            </p>
          </div>
          <div class="btn-row">
            <button class="btn btn-primary" style="flex:1;" id="ui-cc-learn-confirm" disabled>Waiting...</button>
            <button class="btn btn-ghost" id="ui-cc-learn-cancel">Cancel</button>
          </div>
        </div>
        <button class="add-btn" id="ui-cc-add-btn">${iconPlus(9)} Add CC Control</button>
      </div>

      <!-- Notes Tab -->
      <div id="ui-map-note" class="tab-content" data-group="map-tabs">
        <div id="ui-note-list"></div>
        <div id="ui-note-learn-prompt" style="display:none;">
          <div class="empty-state" style="border-color:var(--accent); margin-bottom:8px;">
            <div class="empty-state-icon">🎵</div>
            <div class="empty-state-title" style="color:var(--accent);">Press a note...</div>
            <p style="font-size:10px;color:var(--muted);text-align:left;line-height:1.45;margin:0;">
              Confirm to save a <b style="color:var(--text);">name</b> for this note. Map it to rig signals under <b>Performance</b>.
            </p>
          </div>
          <div class="btn-row">
            <button class="btn btn-primary" style="flex:1;" id="ui-note-learn-confirm" disabled>Waiting...</button>
            <button class="btn btn-ghost" id="ui-note-learn-cancel">Cancel</button>
          </div>
        </div>
        <button class="add-btn" id="ui-note-add-btn">${iconPlus(9)} Add Note Control</button>
      </div>

      <!-- Audio Mapping Tab -->
      <div id="ui-map-audio" class="tab-content" data-group="map-tabs">
        <div class="empty-state">
          <div class="empty-state-icon">🔊</div>
          <div class="empty-state-title">Audio-reactive mapping coming soon</div>
        </div>
      </div>
    </div>
  </div>
</div>`;
  }

  // ─────────────────────────────────────────────────
  // Events
  // ─────────────────────────────────────────────────

  _bindEvents() {
    // Panel toggles
    this._on('ui-btn-connect',       'click', () => this._togglePanel('connect'));
    this._on('ui-btn-midi-activity', 'click', () => this._togglePanel('midi-activity'));
    this._on('ui-btn-mapping',       'click', () => this._togglePanel('mapping'));
    this._on('ui-btn-performance',   'click', () => this._togglePanel('performance'));
    this._on('ui-mode-perform',      'click', () => this._setUiMode('perform'));
    this._on('ui-mode-edit',         'click', () => this._setUiMode('edit'));
    this._on('ui-perf-export',       'click', () => this._exportPerformance());
    this._on('ui-perf-import',       'click', () => document.getElementById('ui-perf-file')?.click());
    document.getElementById('ui-perf-file')?.addEventListener('change', (e) => this._importPerformance(e));
    document.getElementById('ui-perf-name')?.addEventListener('change', (e) =>
      this._patchPerformanceName(e.target.value.trim())
    );
    this._on('ui-perf-add-preset', 'click', () => this._addPreset());
    this._on('ui-perf-back', 'click', () => {
      const inp = document.getElementById('ui-perf-detail-rename-input');
      if (this._renamingPresetKey && inp) {
        this._endPresetRename(true, inp);
      }
      this._perfUiView = 'hub';
      this._renderPerformancePanel();
    });
    this._on('ui-perf-detail-rename', 'click', () => {
      const k = this.state.get('activePresetKey');
      if (k) this._renamePresetDisplayName(k);
    });
    const perfPanel = document.getElementById('ui-panel-performance');
    perfPanel?.addEventListener('focusout', (e) => {
      if (e.target.id !== 'ui-perf-detail-rename-input') return;
      if (!this._renamingPresetKey) return;
      const row = e.target.closest('.perf-detail-title-row');
      const next = e.relatedTarget;
      if (row && next && row.contains(next)) return;
      this._endPresetRename(true, e.target);
    });
    perfPanel?.addEventListener('keydown', (e) => {
      if (e.target.id !== 'ui-perf-detail-rename-input') return;
      if (e.key === 'Enter') {
        e.preventDefault();
        this._endPresetRename(true, e.target);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this._endPresetRename(false, e.target);
      }
    });
    const presetList = document.getElementById('ui-perf-preset-list');
    presetList?.addEventListener('click', (e) => {
      if (e.target.closest('.perf-preset-rename-input')) return;
      const row = e.target.closest('[data-preset-row-key]');
      if (!row) return;
      const k = row.dataset.presetRowKey;
      if (!k) return;
      this.state.set('activePresetKey', k);
      this.app?.switchToPreset?.(k);
      this._perfUiView = 'detail';
      this._renderPerformancePanel();
      this._renderSceneBindings();
    });
    presetList?.addEventListener('focusout', (e) => {
      if (!e.target.classList?.contains('perf-preset-rename-input')) return;
      if (!this._renamingPresetKey) return;
      const row = e.target.closest('.perf-preset-row');
      const next = e.relatedTarget;
      if (row && next && row.contains(next)) return;
      this._endPresetRename(true, e.target);
    });
    presetList?.addEventListener('keydown', (e) => {
      if (!e.target.classList?.contains('perf-preset-rename-input')) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        this._endPresetRename(true, e.target);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this._endPresetRename(false, e.target);
      }
    });

    // Tab switching (event delegation on all tabs)
    document.querySelectorAll('.tab[data-tab-target]').forEach(tab => {
      tab.addEventListener('click', () =>
        this._switchTab(tab, tab.dataset.tabTarget, tab.dataset.tabGroup)
      );
    });

    // MIDI
    this._on('ui-midi-connect-btn',    'click',  () => this._connectMIDI());
    this._on('ui-midi-disconnect-btn', 'click',  () => this._disconnectMIDI());
    this._on('ui-midi-refresh-btn',    'click',  () => this._refreshMIDIDevices());

    // Audio
    this._on('ui-audio-connect-btn',    'click', () => this._connectAudio());
    this._on('ui-audio-disconnect-btn', 'click', () => this._disconnectAudio());
    this._on('ui-audio-refresh-btn',    'click', () => this._refreshAudioDevices());
    this._on('ui-audio-select', 'change', (e) => {
      const iface = this.audioManager.getAvailableInterfaces().find(i => i.id === e.target.value);
      if (iface) this.audioManager.selectInterface(iface);
    });

    // Sensitivity slider
    this._setupSlider(
      'ui-sensitivity-track', 'ui-sensitivity-fill', 'ui-sensitivity-thumb', 'ui-sensitivity-val',
      0, 2, 1.0,
      (v) => this.state.set('audioSensitivity', v),
      (v) => v.toFixed(1)
    );

    // Activity panel
    this._on('ui-activity-clear', 'click',  () => this._clearActivity());
    this._on('ui-activity-pause', 'click',  () => this._togglePause());
    this._on('ui-filter-clock',   'change', (e) => { this._filterClock = e.target.checked; });

    document.querySelectorAll('.chip[data-max]').forEach(chip => {
      chip.addEventListener('click', () => {
        document.querySelectorAll('.chip[data-max]').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        this._maxMessages = parseInt(chip.dataset.max);
      });
    });

    // Mapping
    this._on('ui-cc-add-btn',        'click', () => this._startLearnCC());
    this._on('ui-cc-learn-cancel',   'click', () => this._cancelLearn());
    this._on('ui-note-add-btn',      'click', () => this._startLearnNote());
    this._on('ui-note-learn-cancel', 'click', () => this._cancelLearn());
  }

  _bindState() {
    this.state.subscribe('midiConnected',           (v) => this._updateMIDIStatus(v));
    this.state.subscribe('midiDeviceName',          (v) => this._updateMIDIDeviceName(v));
    this.state.subscribe('audioListening',          (v) => this._updateAudioStatus(v));
    this.state.subscribe('availableAudioInterfaces',(v) => this._populateAudioSelect(v));
    this.state.subscribe('selectedAudioInterface',  (v) => {
      const sel = document.getElementById('ui-audio-select');
      if (sel && v) sel.value = v.id;
    });

    // Audio meters
    this.state.subscribe('audioOverall',   (v) => this._updateMeter('overall', v, 1));
    this.state.subscribe('audioRMS',       (v) => this._updateMeter('rms',     v, 1));
    this.state.subscribe('audioPeak',      (v) => this._updateMeter('peak',    v, 1));
    this.state.subscribe('audioFrequency', (v) => this._updateFreqMeter(v));

    // Mappings + scene bindings (dropdown names)
    this.state.subscribe('midiCCMappings',   () => {
      this._renderCCMappings();
      this._pruneSceneBindings();
      this._renderSceneBindings();
    });
    this.state.subscribe('midiNoteMappings', () => {
      this._renderNoteMappings();
      this._pruneSceneBindings();
      this._renderSceneBindings();
    });
    this.state.subscribe('activeSceneId', () => this._renderSceneBindings());
    this.state.subscribe('uiMode', () => this._applyUiMode());
    this.state.subscribe('performance', () => {
      if (this._tuneParamDragging) {
        this._performanceDirtyDuringTuneDrag = true;
        this._renderSceneBindings();
        this._updatePerformHud();
        return;
      }
      this._renderPerformancePanel();
      this._renderSceneBindings();
      this._updatePerformHud();
    });
    this.state.subscribe('activePresetKey', () => {
      if (this._tuneParamDragging) {
        this._performanceDirtyDuringTuneDrag = true;
        this._renderSceneBindings();
        this._updatePerformHud();
        return;
      }
      this._renderPerformancePanel();
      this._renderSceneBindings();
      this._updatePerformHud();
    });
  }

  refreshPerformancePanel() {
    if (this._tuneParamDragging) {
      this._performanceDirtyDuringTuneDrag = true;
      return;
    }
    this._renderPerformancePanel();
  }

  refreshSceneBindingsPanel() {
    this._renderSceneBindings();
  }

  _setUiMode(mode) {
    if (mode !== 'perform' && mode !== 'edit') return;
    this.state.set('uiMode', mode);
    if (mode === 'perform') {
      document.getElementById('ui-panel-performance')?.classList.remove('show');
      document.getElementById('ui-btn-performance')?.classList.remove('active');
      if (this._openPanel === 'ui-panel-performance') this._openPanel = null;
      document.getElementById('ui-panel-connect')?.classList.remove('show');
      document.getElementById('ui-btn-connect')?.classList.remove('active');
      if (this._openPanel === 'ui-panel-connect') this._openPanel = null;
      document.getElementById('ui-panel-midi-activity')?.classList.remove('show');
      document.getElementById('ui-btn-midi-activity')?.classList.remove('active');
      if (this._openPanel === 'ui-panel-midi-activity') this._openPanel = null;
    }
  }

  _applyUiMode() {
    const mode = this.state.get('uiMode');
    const root = document.getElementById('ui-root');
    if (root) {
      root.classList.toggle('mode-perform', mode === 'perform');
      root.classList.toggle('mode-edit', mode === 'edit');
    }
    document.getElementById('ui-mode-perform')?.classList.toggle('active', mode === 'perform');
    document.getElementById('ui-mode-edit')?.classList.toggle('active', mode === 'edit');
    document.getElementById('ui-perform-hud')?.classList.toggle('show', mode === 'perform');
    this._updatePerformHud();
  }

  _updatePerformHud() {
    const perf = this.state.get('performance');
    const key = this.state.get('activePresetKey');
    const pr = perf?.presets?.[key];
    const elP = document.getElementById('ui-hud-preset');
    const elS = document.getElementById('ui-hud-scene');
    const elPc = document.getElementById('ui-hud-pc');
    if (elP) elP.textContent = key ? presetDisplayLabel(key, pr) : '—';
    if (elS) elS.textContent = pr?.scene || '—';
    if (elPc) elPc.textContent = String(pr?.programNumber ?? 0);
  }

  _rebuildProgramMap(perf) {
    const map = {};
    for (const [pid, pr] of Object.entries(perf.presets || {})) {
      const n = pr.programNumber ?? 0;
      map[String(n)] = pid;
    }
    perf.programMap = map;
  }

  _patchActivePreset(patch) {
    const perf = JSON.parse(JSON.stringify(this.state.get('performance')));
    const key = this.state.get('activePresetKey');
    if (!perf.presets?.[key]) return;
    perf.presets[key] = { ...perf.presets[key], ...patch };
    this._rebuildProgramMap(perf);
    this.state.set('performance', perf);
  }

  _applyPerfBinding(controlKey, value) {
    const activeKey = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const pr = perf?.presets?.[activeKey];
    if (!pr) return;
    const meta = getSceneMeta(pr.scene);
    const nextPreset = setUnifiedControlBinding(meta, pr, controlKey, value);
    this._patchActivePreset(nextPreset);
    const nextBindings = {
      ...(meta?.defaultBindings || {}),
      ...(nextPreset?.bindings || {}),
    };
    this.state.set('sceneBindings', nextBindings);
    this.app?.updateActiveBindingsFromState?.();
  }

  _patchPerformanceName(name) {
    const perf = JSON.parse(JSON.stringify(this.state.get('performance')));
    perf.name = name;
    this.state.set('performance', perf);
  }

  _renamePresetDisplayName(key) {
    if (!key) return;
    const perf = this.state.get('performance');
    if (!perf?.presets?.[key]) return;
    if (this._renamingPresetKey && this._renamingPresetKey !== key) {
      this._endPresetRename(true);
    }
    if (this._renamingPresetKey === key) {
      this._endPresetRename(false);
      return;
    }
    this._renamingPresetKey = key;
    this._renderPerformancePanel();
    this._renderSceneBindings();
    queueMicrotask(() => {
      const inp = document.getElementById('ui-perf-detail-rename-input');
      inp?.focus();
      inp?.select();
    });
  }

  /**
   * @param {boolean} save — if true, apply trimmed input when non-empty
   * @param {HTMLInputElement} [inputEl]
   */
  _endPresetRename(save, inputEl) {
    const key = this._renamingPresetKey;
    if (!key) return;
    const inp =
      inputEl ||
      document.getElementById('ui-perf-detail-rename-input') ||
      document.querySelector('#ui-perf-preset-list .perf-preset-rename-input');
    const raw = inp?.value ?? '';
    this._renamingPresetKey = null;
    if (save) {
      const t = raw.trim();
      if (t) {
        const perf = JSON.parse(JSON.stringify(this.state.get('performance')));
        if (perf.presets?.[key]) {
          perf.presets[key] = { ...perf.presets[key], displayName: t };
          this.state.set('performance', perf);
          this._updatePerformHud();
        }
      }
    }
    this._renderPerformancePanel();
    this._renderSceneBindings();
  }

  _exportPerformance() {
    const data = this.app?.exportPerformance?.();
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(data.name || 'performance').replace(/[^\w\-]+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async _importPerformance(ev) {
    const file = ev.target?.files?.[0];
    ev.target.value = '';
    if (!file || !this.app?.loadPerformance) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      this.app.loadPerformance(data);
      this._renderPerformancePanel();
      this._renderCCMappings();
      this._renderNoteMappings();
      this._renderSceneBindings();
    } catch (err) {
      console.error('Import performance failed', err);
    }
  }

  _renderPerformancePanel() {
    const perf = this.state.get('performance');
    if (this._renamingPresetKey && !perf?.presets?.[this._renamingPresetKey]) {
      this._renamingPresetKey = null;
    }
    const nameInp = document.getElementById('ui-perf-name');
    if (nameInp && document.activeElement !== nameInp) {
      nameInp.value = perf?.name ?? '';
    }

    const list = document.getElementById('ui-perf-preset-list');
    const hubView = document.getElementById('ui-perf-hub-view');
    const detailView = document.getElementById('ui-perf-detail-view');
    const sceneEl = document.getElementById('ui-perf-detail-scene');
    if (!list || !hubView || !detailView || !sceneEl) return;

    const activeKey = this.state.get('activePresetKey');
    let pr = perf?.presets?.[activeKey];
    if (this._perfUiView === 'detail' && !pr) {
      this._perfUiView = 'hub';
    }
    pr = perf?.presets?.[activeKey];
    const showDetail = this._perfUiView === 'detail' && !!pr;
    hubView.hidden = showDetail;
    detailView.hidden = !showDetail;

    const presetKeys = Object.keys(perf?.presets || {});

    list.innerHTML = presetKeys
      .map((k) => {
        const preset = perf.presets[k];
        const plain = presetDisplayLabel(k, preset);
        const label = escapeAttr(plain);
        const active = k === activeKey ? ' perf-preset-row--active' : '';
        const renaming = k === this._renamingPresetKey ? ' perf-preset-row--renaming' : '';
        const main = renaming
          ? `<input type="text" class="map-field perf-preset-rename-input" data-rename-input-key="${escapeAttr(k)}" value="${escapeAttr(plain)}" aria-label="Preset display name" />`
          : `<button type="button" class="perf-preset-row-select">${label}</button>`;
        const chevron = renaming
          ? ''
          : `<span class="perf-preset-row-chevron" aria-hidden="true">${iconChevronRight(16)}</span>`;
        return `<div class="perf-preset-row${active}${renaming}" data-preset-row-key="${escapeAttr(k)}">
  ${main}
  ${chevron}
</div>`;
      })
      .join('');

    const titleSlot = document.getElementById('ui-perf-detail-title-slot');
    const renameHeaderBtn = document.getElementById('ui-perf-detail-rename');
    if (titleSlot) {
      if (showDetail && pr && activeKey) {
        const plain = presetDisplayLabel(activeKey, pr);
        if (this._renamingPresetKey === activeKey) {
          titleSlot.innerHTML = `<input type="text" class="map-field perf-preset-rename-input" id="ui-perf-detail-rename-input" data-rename-input-key="${escapeAttr(activeKey)}" value="${escapeAttr(plain)}" aria-label="Preset display name" />`;
          if (renameHeaderBtn) renameHeaderBtn.hidden = true;
        } else {
          titleSlot.innerHTML = `<span class="perf-detail-title-text">${escapeAttr(plain)}</span>`;
          if (renameHeaderBtn) renameHeaderBtn.hidden = false;
        }
      } else {
        titleSlot.innerHTML = '';
        if (renameHeaderBtn) renameHeaderBtn.hidden = false;
      }
    }

    const controlsEl = document.getElementById('ui-perf-controls');
    if (!controlsEl) return;

    if (!pr) {
      sceneEl.innerHTML =
        '<p class="perf-empty-hint">Select a preset on the hub to edit Controls and Settings.</p>';
      controlsEl.innerHTML = '';
      const envPanelEmpty = document.getElementById('ui-perf-env-panel');
      if (envPanelEmpty) envPanelEmpty.innerHTML = '';
      return;
    }

    const sceneIds = listSceneIds();
    const sceneOpts = sceneIds
      .map(
        (id) =>
          `<option value="${escapeAttr(id)}"${id === pr.scene ? ' selected' : ''}>${escapeAttr(id)}</option>`
      )
      .join('');

    sceneEl.innerHTML = `
      <div class="field">
        <div class="field-label">Scene (Three.js module)</div>
        <select class="map-field" id="ui-perf-scene" style="width:100%;">${sceneOpts}</select>
      </div>
      <div class="field" style="margin-top:10px;">
        <div class="field-label">MIDI program # (0–127)</div>
        <input type="number" class="map-field" id="ui-perf-pc" min="0" max="127" value="${Number(pr.programNumber ?? 0)}" />
      </div>
      <div class="field" style="margin-top:10px;">
        <div class="field-label">Transition</div>
        <select class="map-field" id="ui-perf-transition" style="width:100%;">
          ${['cut', 'fade', 'flash']
            .map((t) => `<option value="${t}"${(pr.transition || 'cut') === t ? ' selected' : ''}>${t}</option>`)
            .join('')}
        </select>
      </div>`;

    const meta = getSceneMeta(pr.scene);
    const bindings = { ...(meta?.defaultBindings || {}), ...(pr.bindings || {}) };
    const mergedParams = { ...(meta?.defaultParams || {}), ...(pr.params || {}) };
    this._renderUnifiedPerfControls(controlsEl, meta, pr, mergedParams, bindings);

    const perfDoc = this.state.get('performance');
    const envPanel = document.getElementById('ui-perf-env-panel');
    if (envPanel) this._renderGlobalEnvPanel(envPanel, perfDoc, pr);

    const sceneSel = document.getElementById('ui-perf-scene');
    if (sceneSel) {
      sceneSel.onchange = (e) => {
        const sid = e.target.value;
        const newMeta = getSceneMeta(sid);
        this._patchActivePreset({
          scene: sid,
          bindings: { ...(newMeta?.defaultBindings || {}) },
          params: { ...(newMeta?.defaultParams || {}) },
        });
        this.app?.switchToPreset?.(this.state.get('activePresetKey'));
        this._renderPerformancePanel();
        this._renderSceneBindings();
      };
    }
    const pcInp = document.getElementById('ui-perf-pc');
    if (pcInp) {
      pcInp.onchange = (e) => {
        const n = Math.max(0, Math.min(127, parseInt(e.target.value, 10) || 0));
        this._patchActivePreset({ programNumber: n });
        this._updatePerformHud();
      };
    }
    const transSel = document.getElementById('ui-perf-transition');
    if (transSel) {
      transSel.onchange = (e) => {
        this._patchActivePreset({ transition: e.target.value });
      };
    }
  }

  _applyGlobalEnvManual(controlKey, value) {
    const activeKey = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const pr = perf?.presets?.[activeKey];
    if (!pr || !activeKey) return;
    const prev = pr.envOverrides && typeof pr.envOverrides === 'object' ? pr.envOverrides : {};
    this._patchActivePreset({ envOverrides: { ...prev, [controlKey]: value } });
    this.app?.applyActivePresetParams?.();
  }

  _applyGlobalEnvBinding(controlKey, bindingName) {
    const activeKey = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const pr = perf?.presets?.[activeKey];
    if (!pr || !activeKey) return;
    const nextPreset = setGlobalEnvControlBinding(pr, controlKey, bindingName);
    this._patchActivePreset(nextPreset);
    this.app?.updateActiveBindingsFromState?.();
  }

  /**
   * @param {HTMLElement} envPanel
   * @param {object} perfDoc
   * @param {object} pr
   */
  _renderGlobalEnvPanel(envPanel, perfDoc, pr) {
    const sceneLabel = getSceneMeta(pr.scene)?.label || pr.scene || '—';
    const counts = summarizeGlobalEnvUsageForScene(pr.scene);
    envPanel.innerHTML = `
      <div class="perf-env-scene-banner">
        <span class="perf-env-scene-meta">
          <span class="perf-env-scene-kicker">Environment →</span>
          <strong class="perf-env-scene-title">${escapeAttr(sceneLabel)}</strong>
          <code class="perf-env-scene-id">${escapeAttr(String(pr.scene || ''))}</code>
        </span>
        <span class="perf-env-scene-counts" title="Counts of shared environment keys for this scene type">
          ${counts.scene} in-scene · ${counts.post} bloom · ${counts.unused} ignored here
        </span>
      </div>
      <p class="perf-controls-future-note">Each row shows whether this scene reads the control, where the value comes from (preset vs shared performance vs app default), and if live MIDI is bound. Manual sliders write this preset’s <code class="perf-env-inline-code">envOverrides</code> only.</p>
      <div id="ui-perf-env-controls"></div>`;
    const inner = document.getElementById('ui-perf-env-controls');
    if (inner) this._renderGlobalEnvControls(inner, perfDoc, pr);
  }

  /**
   * @param {HTMLElement} controlsEl
   * @param {object} perfDoc
   * @param {object} preset
   */
  _renderGlobalEnvControls(controlsEl, perfDoc, preset) {
    const controlDefs = deriveGlobalEnvControlDefs(perfDoc, preset);
    const mergedEnv = getMergedEffectiveEnv(perfDoc, preset);
    const bindings = { ...(preset?.bindings || {}) };

    if (!controlDefs.length) {
      controlsEl.innerHTML = '<p class="perf-empty-hint">No environment controls.</p>';
      return;
    }

    controlsEl.innerHTML = `
      <div class="section-label perf-controls-heading">Environment</div>
      <div class="perf-controls-list">
        ${controlDefs
          .map((control) => {
            const paramKey = control.legacy.paramKey;
            const bindingKey = control.legacy.bindingKey;
            const domBase = perfEnvControlDomId(control.key);
            const bindingValue = bindingKey
              ? String(bindings?.[bindingKey] || control.bindingDefault || '').trim()
              : '';
            const isFutureControl = control.origin === 'discovered';
            const statusText = isFutureControl
              ? bindingValue
                ? bindingValue
                : 'Unbound by default'
              : bindingValue || (control.bindable ? 'Manual' : 'Manual only');
            const statusClass = bindingValue
              ? ' perf-control-status--bound'
              : isFutureControl
                ? ' perf-control-status--future'
                : '';
            const manualValue = paramKey ? mergedEnv?.[paramKey] : undefined;
            const sourceLabel = formatControlSourceLabel(control.source);
            const metaChips = isFutureControl
              ? `
                <div class="perf-control-meta">
                  <span class="perf-control-chip">${escapeAttr(sourceLabel)}</span>
                  ${control.confidence ? `<span class="perf-control-chip">${escapeAttr(control.confidence)} confidence</span>` : ''}
                </div>`
              : '';

            let manualHtml = '';
            if (isFutureControl) {
              manualHtml = `
                <div class="perf-control-note">
                  Reserved ${escapeAttr(sourceLabel.toLowerCase())} control.
                </div>`;
            } else if (paramKey) {
              if (control.kind === 'number' || control.kind === 'color') {
                manualHtml = `
                  <div class="perf-control-manual">
                    <div class="slider-row perf-control-slider-row">
                      <div class="slider-track" id="${domBase}-track">
                        <div class="slider-fill" id="${domBase}-fill"></div>
                        <div class="slider-thumb" id="${domBase}-thumb"></div>
                      </div>
                      <div class="slider-value perf-control-slider-value" id="${domBase}-val"></div>
                    </div>
                  </div>`;
              } else if (control.kind === 'boolean') {
                manualHtml = `
                  <div class="perf-control-field">
                    <select class="map-field ui-perf-env-bool" data-perf-env-bool="${escapeAttr(control.key)}">
                      <option value="false"${manualValue === false ? ' selected' : ''}>Off</option>
                      <option value="true"${manualValue === true ? ' selected' : ''}>On</option>
                    </select>
                  </div>`;
              } else if (control.kind === 'enum' && Array.isArray(control.ui?.options) && control.ui.options.length) {
                manualHtml = `
                  <div class="perf-control-field">
                    <select class="map-field ui-perf-env-enum" data-perf-env-enum="${escapeAttr(control.key)}">
                      ${control.ui.options
                        .map((option) => {
                          const s = String(option);
                          return `<option value="${escapeAttr(s)}"${s === String(manualValue ?? '') ? ' selected' : ''}>${escapeAttr(s)}</option>`;
                        })
                        .join('')}
                    </select>
                  </div>`;
              } else {
                manualHtml = `
                  <div class="perf-control-field">
                    <input type="text" class="map-field ui-perf-env-text" data-perf-env-text="${escapeAttr(control.key)}" value="${escapeAttr(String(manualValue ?? ''))}" />
                  </div>`;
              }
            } else {
              manualHtml = '<div class="perf-control-note">Binding-only control.</div>';
            }

            const bindingHtml = control.bindable
              ? `
                <div class="perf-control-field">
                  <div class="field-label">Live input</div>
                  <select class="map-field perf-env-bind-select" data-perf-env-bind="${escapeAttr(control.key)}">
                    ${this._controlNameSelectOptions(bindingValue)}
                  </select>
                </div>`
              : '';

            const usage =
              paramKey && !isFutureControl
                ? getGlobalEnvKeyUsage(preset.scene, paramKey)
                : null;
            const valueSource =
              paramKey && !isFutureControl
                ? getGlobalEnvValueSource(perfDoc, preset, paramKey)
                : null;
            const envLiveBound =
              !!paramKey &&
              !isFutureControl &&
              GLOBAL_ENV_BINDING_TARGETS.some((t) => t.key === paramKey) &&
              !!bindingValue;

            const debugHtml =
              usage && valueSource
                ? `<div class="perf-env-debug">
                  <span class="perf-env-usage perf-env-usage--${usage.kind}" title="${escapeAttr(usage.hint || '')}">${escapeAttr(formatGlobalEnvUsageLabel(usage))}</span>
                  <span class="perf-env-source">${escapeAttr(formatGlobalEnvSourceLabel(valueSource))}</span>
                  ${envLiveBound ? `<span class="perf-env-live">Live <span class="perf-env-live-name">${escapeAttr(bindingValue)}</span> may override each frame.</span>` : ''}
                </div>`
                : '';

            return `
              <div class="perf-control-row${isFutureControl ? ' perf-control-row--future' : ''}" data-perf-env-key="${escapeAttr(control.key)}">
                <div class="perf-control-head">
                  <div class="perf-control-title-wrap">
                    <div class="perf-control-title">${escapeAttr(control.label)}</div>
                    ${metaChips}
                  </div>
                  <span class="perf-control-status${statusClass}">${escapeAttr(statusText)}</span>
                </div>
                ${debugHtml}
                ${manualHtml}
                ${bindingHtml}
              </div>`;
          })
          .join('')}
      </div>`;

    controlsEl.querySelectorAll('[data-perf-env-bind]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyGlobalEnvBinding(sel.dataset.perfEnvBind, sel.value);
      });
    });
    controlsEl.querySelectorAll('[data-perf-env-text]').forEach((inp) => {
      inp.addEventListener('change', () => {
        this._applyGlobalEnvManual(inp.dataset.perfEnvText, inp.value);
      });
    });
    controlsEl.querySelectorAll('[data-perf-env-bool]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyGlobalEnvManual(sel.dataset.perfEnvBool, sel.value === 'true');
      });
    });
    controlsEl.querySelectorAll('[data-perf-env-enum]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyGlobalEnvManual(sel.dataset.perfEnvEnum, sel.value);
      });
    });

    const gmeta = getGlobalEnvSceneMeta();
    this._bindPerfEnvUnifiedControlSliders(controlsEl, gmeta, controlDefs, mergedEnv);
  }

  _bindPerfEnvUnifiedControlSliders(controlsEl, meta, controlDefs, mergedParams) {
    for (const control of controlDefs) {
      const paramKey = control.legacy?.paramKey;
      if (!paramKey || (control.kind !== 'number' && control.kind !== 'color')) continue;

      const domBase = perfEnvControlDomId(control.key);
      const track = controlsEl.querySelector(`#${domBase}-track`);
      const fill = controlsEl.querySelector(`#${domBase}-fill`);
      const thumb = controlsEl.querySelector(`#${domBase}-thumb`);
      const valEl = controlsEl.querySelector(`#${domBase}-val`);
      if (!track) continue;

      const defVal = meta?.defaultParams?.[paramKey];
      const legacySpec = defVal !== undefined ? resolveParamSpec(meta, paramKey, defVal) : null;

      if (control.kind === 'color' || legacySpec?.type === 'hue') {
        this._bindHueSlider(
          track,
          fill,
          thumb,
          valEl,
          String(mergedParams[paramKey] ?? defVal ?? '#66aaff'),
          (hex) => this._applyGlobalEnvManual(control.key, hex),
          true
        );
        continue;
      }

      const min = control.ui?.min ?? legacySpec?.min ?? 0;
      const max = control.ui?.max ?? legacySpec?.max ?? 1;
      const step = control.ui?.step ?? legacySpec?.step ?? null;
      const format =
        legacySpec?.format ||
        (max <= 1 && min >= 0
          ? (v) => v.toFixed(2)
          : (v) => String(Math.round(v)));

      this._bindSliderTrack(
        track,
        fill,
        thumb,
        valEl,
        min,
        max,
        Number(mergedParams[paramKey] ?? defVal ?? min),
        step,
        (nv) => this._applyGlobalEnvManual(control.key, nv),
        format,
        true
      );
    }
  }

  _renderUnifiedPerfControls(controlsEl, meta, preset, mergedParams, bindings) {
    const controlDefs = deriveSceneControlDefsForSceneTab(meta, preset);
    if (!controlDefs.length) {
      controlsEl.innerHTML = '<p class="perf-empty-hint">No controls available for this preset.</p>';
      return;
    }

    const futureControlCount = controlDefs.filter((control) => control.origin === 'discovered').length;

    controlsEl.innerHTML = `
      <div class="section-label perf-controls-heading">Unified controls</div>
      ${futureControlCount
        ? `<p class="perf-controls-future-note">${futureControlCount} imported/discovered control${futureControlCount === 1 ? '' : 's'} reserved here. They stay unbound by default until a runtime adapter wires them into params or signals.</p>`
        : ''}
      <div class="perf-controls-list">
        ${controlDefs
          .map((control) => {
            const paramKey = control.legacy.paramKey;
            const bindingKey = control.legacy.bindingKey;
            const domBase = perfControlDomId(control.key);
            const bindingValue = bindingKey
              ? String(bindings?.[bindingKey] || control.bindingDefault || '').trim()
              : '';
            const isFutureControl = control.origin === 'discovered';
            const statusText = isFutureControl
              ? bindingValue
                ? bindingValue
                : 'Unbound by default'
              : bindingValue || (control.bindable ? 'Manual' : 'Manual only');
            const statusClass = bindingValue
              ? ' perf-control-status--bound'
              : isFutureControl
                ? ' perf-control-status--future'
                : '';
            const manualValue = paramKey ? mergedParams?.[paramKey] : undefined;
            const sourceLabel = formatControlSourceLabel(control.source);
            const metaChips = isFutureControl
              ? `
                <div class="perf-control-meta">
                  <span class="perf-control-chip">${escapeAttr(sourceLabel)}</span>
                  ${control.confidence ? `<span class="perf-control-chip">${escapeAttr(control.confidence)} confidence</span>` : ''}
                </div>`
              : '';

            let manualHtml = '';
            if (isFutureControl) {
              manualHtml = `
                <div class="perf-control-note">
                  Reserved ${escapeAttr(sourceLabel.toLowerCase())} control. It appears in the unified list now, but remains unbound until discovery/import runtime wiring is implemented.
                </div>`;
            } else if (paramKey) {
              if (control.kind === 'number' || control.kind === 'color') {
                manualHtml = `
                  <div class="perf-control-manual">
                    <div class="slider-row perf-control-slider-row">
                      <div class="slider-track" id="${domBase}-track">
                        <div class="slider-fill" id="${domBase}-fill"></div>
                        <div class="slider-thumb" id="${domBase}-thumb"></div>
                      </div>
                      <div class="slider-value perf-control-slider-value" id="${domBase}-val"></div>
                    </div>
                  </div>`;
              } else if (control.kind === 'boolean') {
                manualHtml = `
                  <div class="perf-control-field">
                    <select class="map-field ui-perf-control-bool" data-perf-control-bool="${escapeAttr(control.key)}">
                      <option value="false"${manualValue === false ? ' selected' : ''}>Off</option>
                      <option value="true"${manualValue === true ? ' selected' : ''}>On</option>
                    </select>
                  </div>`;
              } else if (control.kind === 'enum' && Array.isArray(control.ui?.options) && control.ui.options.length) {
                manualHtml = `
                  <div class="perf-control-field">
                    <select class="map-field ui-perf-control-enum" data-perf-control-enum="${escapeAttr(control.key)}">
                      ${control.ui.options
                        .map((option) => {
                          const s = String(option);
                          return `<option value="${escapeAttr(s)}"${s === String(manualValue ?? '') ? ' selected' : ''}>${escapeAttr(s)}</option>`;
                        })
                        .join('')}
                    </select>
                  </div>`;
              } else {
                manualHtml = `
                  <div class="perf-control-field">
                    <input type="text" class="map-field ui-perf-control-text" data-perf-control-text="${escapeAttr(control.key)}" value="${escapeAttr(String(manualValue ?? ''))}" />
                  </div>`;
              }
            } else {
              manualHtml = '<div class="perf-control-note">Binding-only control in the current legacy runtime.</div>';
            }

            const bindingHtml = control.bindable
              ? `
                <div class="perf-control-field">
                  <div class="field-label">Live input</div>
                  <select class="map-field perf-control-bind-select" data-perf-control-bind="${escapeAttr(control.key)}">
                    ${this._controlNameSelectOptions(bindingValue)}
                  </select>
                </div>`
              : '';

            return `
              <div class="perf-control-row${isFutureControl ? ' perf-control-row--future' : ''}" data-perf-control-key="${escapeAttr(control.key)}">
                <div class="perf-control-head">
                  <div class="perf-control-title-wrap">
                    <div class="perf-control-title">${escapeAttr(control.label)}</div>
                    ${metaChips}
                  </div>
                  <span class="perf-control-status${statusClass}">${escapeAttr(statusText)}</span>
                </div>
                ${manualHtml}
                ${bindingHtml}
              </div>`;
          })
          .join('')}
      </div>`;

    controlsEl.querySelectorAll('[data-perf-control-bind]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyPerfBinding(sel.dataset.perfControlBind, sel.value);
      });
    });
    controlsEl.querySelectorAll('[data-perf-control-text]').forEach((inp) => {
      inp.addEventListener('change', () => {
        this._applyUnifiedControlValue(inp.dataset.perfControlText, inp.value);
      });
    });
    controlsEl.querySelectorAll('[data-perf-control-bool]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyUnifiedControlValue(sel.dataset.perfControlBool, sel.value === 'true');
      });
    });
    controlsEl.querySelectorAll('[data-perf-control-enum]').forEach((sel) => {
      sel.addEventListener('change', () => {
        this._applyUnifiedControlValue(sel.dataset.perfControlEnum, sel.value);
      });
    });
    this._bindPerfUnifiedControlSliders(controlsEl, meta, controlDefs, mergedParams);
  }

  _addPreset() {
    const perf = JSON.parse(JSON.stringify(this.state.get('performance')));
    if (!perf.presets) perf.presets = {};
    let i = Object.keys(perf.presets).length;
    let id = `preset_${i}`;
    while (perf.presets[id]) {
      i++;
      id = `preset_${i}`;
    }
    const usedPc = new Set(
      Object.values(perf.presets).map((p) => Number(p.programNumber ?? 0))
    );
    let pc = 0;
    while (usedPc.has(pc)) pc++;
    const nextSlot = Object.keys(perf.presets).length + 1;
    perf.presets[id] = {
      displayName: `Preset ${nextSlot}`,
      scene: 'iridescent_wave',
      programNumber: pc,
      transition: 'cut',
      bindings: { ...(getSceneMeta('iridescent_wave')?.defaultBindings || {}) },
      params: { ...(getSceneMeta('iridescent_wave')?.defaultParams || {}) },
    };
    this._rebuildProgramMap(perf);
    this.state.set('performance', perf);
    this.state.set('activePresetKey', id);
    this.app?.switchToPreset?.(id);
    this._renderPerformancePanel();
    this._renderSceneBindings();
  }

  // ─────────────────────────────────────────────────
  // Panel management
  // ─────────────────────────────────────────────────

  _togglePanel(id) {
    const panelId = `ui-panel-${id}`;
    const btnId   = `ui-btn-${id}`;

    if (this._openPanel === panelId) {
      document.getElementById(panelId)?.classList.remove('show');
      document.getElementById(btnId)?.classList.remove('active');
      this._openPanel = null;
      return;
    }

    if (this._openPanel) {
      document.getElementById(this._openPanel)?.classList.remove('show');
      const prevBtn = this._openPanel.replace('ui-panel-', 'ui-btn-');
      document.getElementById(prevBtn)?.classList.remove('active');
    }

    document.getElementById(panelId)?.classList.add('show');
    document.getElementById(btnId)?.classList.add('active');
    this._openPanel = panelId;

    if (id === 'connect') {
      this._onConnectPanelOpen();
    }
  }

  _onConnectPanelOpen() {
    // If MIDI access hasn't been initialized yet, do it now so the dropdown populates
    if (!this.midiManager.midiAccess) {
      this.midiManager.refreshDevices().then(() => this._populateMIDISelect());
    } else {
      // Always re-read the current inputs — the list may have changed since last open
      this._populateMIDISelect();
    }
  }

  _switchTab(tabEl, targetId, group) {
    tabEl.closest('.tabs').querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
    tabEl.classList.add('active');
    document.querySelectorAll(`[data-group="${group}"]`).forEach(c => c.classList.remove('active'));
    document.getElementById(targetId)?.classList.add('active');
  }

  // ─────────────────────────────────────────────────
  // MIDI panel
  // ─────────────────────────────────────────────────

  async _refreshMIDIDevices() {
    await this.midiManager.refreshDevices();
    this._populateMIDISelect();
  }

  _populateMIDISelect() {
    const inputs      = this.midiManager.getAvailableInputs();
    const sel         = document.getElementById('ui-midi-select');
    const activeName  = this.midiManager.midiInput?.name ?? null;
    if (!sel) return;
    if (!inputs.length) {
      sel.innerHTML = '<option value="">No MIDI devices found</option>';
      return;
    }
    sel.innerHTML = inputs.map(i => {
      const selected = i.name === activeName ? ' selected' : '';
      const label    = i.name === activeName ? `${i.name} ✓` : i.name;
      return `<option value="${i.name}"${selected}>${label}</option>`;
    }).join('');
  }

  _connectMIDI() {
    const sel  = document.getElementById('ui-midi-select');
    const name = sel?.value;
    if (name) {
      this.midiManager.connectToInput(name);
    } else {
      this.midiManager.connect().then(() => this._populateMIDISelect());
    }
  }

  _disconnectMIDI() {
    this.midiManager.disconnect();
  }

  _updateMIDIStatus(connected) {
    const dot  = document.getElementById('ui-midi-dot');
    const lbl  = document.getElementById('ui-midi-label');
    const tag  = document.getElementById('ui-midi-status-tag');
    const icon = document.getElementById('ui-midi-card-icon');

    if (dot)  dot.classList.toggle('connected', connected);
    if (lbl)  lbl.textContent = connected ? 'MIDI Connected' : 'No MIDI';
    if (tag)  { tag.textContent = connected ? 'Live' : 'Off'; tag.className = `status-tag ${connected ? 'connected' : 'disconnected'}`; }
    if (icon) icon.className = `status-card-icon ${connected ? 'ok' : 'err'}`;

    if (connected) this._populateMIDISelect();

    const connEl = document.getElementById('ui-conn-status');
    if (connEl) { connEl.textContent = connected ? 'Connected' : 'Disconnected'; connEl.className = `conn-value ${connected ? '' : 'danger'}`; }
  }

  _updateMIDIDeviceName(name) {
    const sub = document.getElementById('ui-midi-device-sub');
    const dev = document.getElementById('ui-conn-device');
    if (sub) sub.textContent = name || 'No device connected';
    if (dev) dev.textContent = name || 'None';
  }

  // ─────────────────────────────────────────────────
  // Audio panel
  // ─────────────────────────────────────────────────

  _connectAudio()    { this.state.set('audioEnabled', true);  }
  _disconnectAudio() { this.state.set('audioEnabled', false); }

  async _refreshAudioDevices() {
    await this.audioManager.refreshInterfaces();
  }

  _populateAudioSelect(ifaces) {
    const sel = document.getElementById('ui-audio-select');
    const sub = document.getElementById('ui-audio-device-sub');
    if (sel) {
      sel.innerHTML = ifaces?.length
        ? ifaces.map(i => `<option value="${i.id}">${i.label}</option>`).join('')
        : '<option value="">No interfaces available</option>';
    }
    if (sub) sub.textContent = ifaces?.length ? `${ifaces.length} device(s) found` : 'No interfaces available';
  }

  _updateAudioStatus(listening) {
    const dot  = document.getElementById('ui-audio-dot');
    const lbl  = document.getElementById('ui-audio-label');
    const tag  = document.getElementById('ui-audio-status-tag');
    const icon = document.getElementById('ui-audio-card-icon');
    const iface = this.state.get('selectedAudioInterface');

    if (dot)  dot.classList.toggle('connected', listening);
    if (lbl)  lbl.textContent = listening ? (iface?.label || 'Audio Active') : 'No Audio';
    if (tag)  { tag.textContent = listening ? 'Live' : 'Off'; tag.className = `status-tag ${listening ? 'connected' : 'disconnected'}`; }
    if (icon) icon.className = `status-card-icon ${listening ? 'ok' : 'err'}`;
  }

  _updateMeter(id, value, max) {
    const pct = Math.min(value / max, 1);
    const el  = document.getElementById(`ui-meter-${id}`);
    const bar = document.getElementById(`ui-meter-${id}-bar`);
    if (el)  el.textContent  = value.toFixed(2);
    if (bar) bar.style.width = `${(pct * 100).toFixed(1)}%`;
  }

  _updateFreqMeter(hz) {
    const el  = document.getElementById('ui-meter-freq');
    const bar = document.getElementById('ui-meter-freq-bar');
    if (el)  el.textContent  = hz > 1000 ? `${(hz / 1000).toFixed(1)}kHz` : `${Math.round(hz)}Hz`;
    if (bar) bar.style.width = `${Math.min((hz / 20000) * 100, 100).toFixed(1)}%`;
  }

  // ─────────────────────────────────────────────────
  // MIDI Activity (public — called from App)
  // ─────────────────────────────────────────────────

  onMIDIMessage(data) {
    const status = data[0];

    // System real-time
    if (status >= 0xF8) {
      if (status === 0xF8 && this._filterClock) return;
      this._msgStats.system++;
      if (!this._monitorPaused) {
        const labels = { 0xF8: 'CLOCK', 0xFA: 'START', 0xFB: 'CONTINUE', 0xFC: 'STOP' };
        this._addActivityRow(`<span style="color:var(--muted)">${labels[status] || 'SYS'}</span>`);
      }
      this._recordActivity();
      return;
    }

    const channel = (status & 0x0F) + 1;
    const type    = status & 0xF0;
    let   row     = '';

    if (type === 0x90 && data[2] > 0) {
      this._msgStats.notes++;
      row = `<span style="color:var(--accent)">NOTE ON </span>\u00a0 Ch${channel}\u00a0 <b>${midiNoteName(data[1])}</b>\u00a0 vel:<span style="color:var(--accent2)">${data[2]}</span>`;
    } else if (type === 0x80 || (type === 0x90 && data[2] === 0)) {
      this._msgStats.notes++;
      row = `<span style="color:var(--muted)">NOTE OFF</span>\u00a0 Ch${channel}\u00a0 <b>${midiNoteName(data[1])}</b>`;
    } else if (type === 0xB0) {
      this._msgStats.cc++;
      row = `<span style="color:var(--accent3)">CC\u00a0\u00a0\u00a0\u00a0\u00a0</span>\u00a0 Ch${channel}\u00a0 #${data[1]}\u00a0 val:<span style="color:var(--accent3)">${data[2]}</span>`;
    } else if (type === 0xC0) {
      this._msgStats.system++;
      row = `<span style="color:var(--accent2)">PROG\u00a0CHG</span>\u00a0 Ch${channel}\u00a0 #${data[1]}`;
    } else if (type === 0xE0) {
      this._msgStats.pitchBend++;
      const pb = ((data[2] << 7) | data[1]) - 8192;
      row = `<span style="color:var(--midi-blue)">PITCHBEND</span>\u00a0 Ch${channel}\u00a0 ${pb}`;
    } else {
      row = `<span style="color:var(--muted)">0x${status.toString(16).toUpperCase()}</span>`;
    }

    this._updateStats();
    this._recordActivity();
    if (!this._monitorPaused) this._addActivityRow(row);
  }

  _addActivityRow(html) {
    const stream = document.getElementById('ui-midi-stream');
    if (!stream) return;

    if (this._activityMessages.length === 0) stream.innerHTML = '';

    this._activityMessages.push(html);
    if (this._activityMessages.length > this._maxMessages) {
      this._activityMessages.shift();
      stream.firstChild?.remove();
    }

    const row = document.createElement('div');
    row.style.cssText = 'padding:1px 0;border-bottom:1px solid var(--border);font-size:10px;white-space:nowrap;';
    row.innerHTML = html;
    stream.appendChild(row);
    stream.scrollTop = stream.scrollHeight;

    const count = document.getElementById('ui-activity-count');
    if (count) count.textContent = `${this._activityMessages.length} messages`;
  }

  _recordActivity() {
    this._msgsThisSec++;
    this._lastActivityTime = new Date();
    const la = document.getElementById('ui-conn-lastact');
    if (la) la.textContent = this._lastActivityTime.toLocaleTimeString();
  }

  _updateMsgRate() {
    const el = document.getElementById('ui-conn-msgrate');
    if (el) el.textContent = this._msgsThisSec;
    this._msgsThisSec = 0;
  }

  _updateStats() {
    const ids = ['cc', 'notes', 'pb', 'sys'];
    const vals = [this._msgStats.cc, this._msgStats.notes, this._msgStats.pitchBend, this._msgStats.system];
    ids.forEach((id, i) => {
      const el = document.getElementById(`ui-stat-${id}`);
      if (el) { el.textContent = vals[i]; el.className = `stat-value ${vals[i] > 0 ? '' : 'zero'}`; }
    });
  }

  _clearActivity() {
    this._activityMessages = [];
    this._msgStats = { cc: 0, notes: 0, pitchBend: 0, system: 0 };
    this._updateStats();
    const stream = document.getElementById('ui-midi-stream');
    if (stream) { stream.innerHTML = 'No MIDI activity detected'; stream.style.display = ''; }
    const count = document.getElementById('ui-activity-count');
    if (count) count.textContent = '0 messages';
  }

  _togglePause() {
    this._monitorPaused = !this._monitorPaused;
    const btn = document.getElementById('ui-activity-pause');
    if (btn) btn.textContent = this._monitorPaused ? 'Resume' : 'Pause';
  }

  // ─────────────────────────────────────────────────
  // Mapping
  // ─────────────────────────────────────────────────

  /** Keeps `sceneBindings` keys in sync with the active scene; binding UI is in the Performance panel. */
  _renderSceneBindings() {
    this._ensureSceneBindingKeys();
  }

  _renderCCMappings() {
    const mappings = this.state.get('midiCCMappings') || {};
    const list     = document.getElementById('ui-cc-list');
    if (!list) return;

    const entries = Object.entries(mappings).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) {
      list.innerHTML = `
        <div class="empty-state" style="margin-bottom:8px;">
          <div class="empty-state-icon">🎛️</div>
          <div class="empty-state-title">No CC mappings yet</div>
        </div>`;
      return;
    }

    list.innerHTML = entries.map(([id, m], i) => {
      const displayName =
        m.name != null && String(m.name).trim() !== ''
          ? String(m.name).trim()
          : `control_${i + 1}`;
      return `
        <div style="display:flex;align-items:flex-start;gap:6px;padding:6px 0;border-bottom:1px solid var(--border);">
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <span style="font-size:10px;color:var(--muted);font-family:JetBrains Mono,monospace;">Ch${m.channel + 1}</span>
              <span style="background:rgba(0,255,136,0.1);border:1px solid var(--accent);border-radius:3px;padding:2px 6px;font-size:10px;color:var(--accent);font-family:JetBrains Mono,monospace;">CC ${m.value}</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;width:100%;">
              <span style="font-size:9px;color:var(--muted);font-family:var(--mono);flex-shrink:0;">name</span>
              <input type="text" class="map-field" data-map-cc-name="${id}" value="${escapeAttr(displayName)}" placeholder="control_1" />
            </div>
          </div>
          <button class="btn btn-ghost btn-icon" style="height:24px;width:24px;flex-shrink:0;margin-top:2px;" data-del-cc="${id}">${iconX(8)}</button>
        </div>`;
    }).join('');

    list.querySelectorAll('[data-del-cc]').forEach(btn =>
      btn.addEventListener('click', () => this._deleteMapping('cc', btn.dataset.delCc))
    );
    list.querySelectorAll('[data-map-cc-name]').forEach((inp) => {
      inp.addEventListener('change', () =>
        this._updateMappingField('cc', inp.dataset.mapCcName, { name: inp.value })
      );
    });
  }

  _renderNoteMappings() {
    const mappings = this.state.get('midiNoteMappings') || {};
    const list     = document.getElementById('ui-note-list');
    if (!list) return;

    const entries = Object.entries(mappings).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) {
      list.innerHTML = `
        <div class="empty-state" style="margin-bottom:8px;">
          <div class="empty-state-icon">🎵</div>
          <div class="empty-state-title">No note mappings yet</div>
        </div>`;
      return;
    }

    list.innerHTML = entries.map(([id, m], i) => {
      const displayName =
        m.name != null && String(m.name).trim() !== ''
          ? String(m.name).trim()
          : `control_${i + 1}`;
      return `
        <div style="display:flex;align-items:flex-start;gap:6px;padding:6px 0;border-bottom:1px solid var(--border);">
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <span style="font-size:10px;color:var(--muted);font-family:JetBrains Mono,monospace;">Ch${m.channel + 1}</span>
              <span style="background:rgba(124,58,237,0.1);border:1px solid var(--accent2);border-radius:3px;padding:2px 6px;font-size:10px;color:var(--accent2);font-family:JetBrains Mono,monospace;">${midiNoteName(m.value)}</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;width:100%;">
              <span style="font-size:9px;color:var(--muted);font-family:var(--mono);flex-shrink:0;">name</span>
              <input type="text" class="map-field" data-map-note-name="${id}" value="${escapeAttr(displayName)}" placeholder="control_1" />
            </div>
          </div>
          <button class="btn btn-ghost btn-icon" style="height:24px;width:24px;flex-shrink:0;margin-top:2px;" data-del-note="${id}">${iconX(8)}</button>
        </div>`;
    }).join('');

    list.querySelectorAll('[data-del-note]').forEach(btn =>
      btn.addEventListener('click', () => this._deleteMapping('note', btn.dataset.delNote))
    );
    list.querySelectorAll('[data-map-note-name]').forEach((inp) => {
      inp.addEventListener('change', () =>
        this._updateMappingField('note', inp.dataset.mapNoteName, { name: inp.value })
      );
    });
  }

  _startLearnCC() {
    this._cancelLearn();
    const prompt  = document.getElementById('ui-cc-learn-prompt');
    const addBtn  = document.getElementById('ui-cc-add-btn');
    const confirm = document.getElementById('ui-cc-learn-confirm');
    if (prompt) prompt.style.display = 'block';
    if (addBtn) addBtn.style.display = 'none';

    let pending = null;

    this._learnCb = (ccNumber, _val, channel) => {
      pending = { ccNumber, channel };
      if (confirm) {
        confirm.textContent = `Assign CC ${ccNumber} (Ch${channel + 1})`;
        confirm.disabled    = false;
        confirm.onclick     = () => {
          if (!pending) return;
          const mappings = { ...(this.state.get('midiCCMappings') || {}) };
          const id = `cc-${pending.ccNumber}-${pending.channel}`;
          mappings[id] = {
            channel: pending.channel,
            value: pending.ccNumber,
            name: nextControlName(this.state),
          };
          this.state.set('midiCCMappings', mappings);
          this._cancelLearn();
        };
      }
    };

    this.midiEventHandler.addLearnCCListener(this._learnCb);
    this._learnType = 'cc';
  }

  _startLearnNote() {
    this._cancelLearn();
    const prompt  = document.getElementById('ui-note-learn-prompt');
    const addBtn  = document.getElementById('ui-note-add-btn');
    const confirm = document.getElementById('ui-note-learn-confirm');
    if (prompt) prompt.style.display = 'block';
    if (addBtn) addBtn.style.display = 'none';

    let pending = null;

    this._learnCb = (note, _vel, isNoteOn, channel) => {
      if (!isNoteOn) return;
      pending = { note, channel };
      if (confirm) {
        confirm.textContent = `Assign ${midiNoteName(note)} (Ch${channel + 1})`;
        confirm.disabled    = false;
        confirm.onclick     = () => {
          if (!pending) return;
          const mappings = { ...(this.state.get('midiNoteMappings') || {}) };
          const id = `note-${pending.note}-${pending.channel}`;
          mappings[id] = {
            channel: pending.channel,
            value: pending.note,
            name: nextControlName(this.state),
          };
          this.state.set('midiNoteMappings', mappings);
          this._cancelLearn();
        };
      }
    };

    this.midiEventHandler.addLearnNoteListener(this._learnCb);
    this._learnType = 'note';
  }

  _cancelLearn() {
    if (this._learnCb) {
      if (this._learnType === 'cc')   this.midiEventHandler.removeLearnCCListener(this._learnCb);
      if (this._learnType === 'note') this.midiEventHandler.removeLearnNoteListener(this._learnCb);
      this._learnCb   = null;
      this._learnType = null;
    }
    ['cc', 'note'].forEach(t => {
      const prompt = document.getElementById(`ui-${t}-learn-prompt`);
      const addBtn = document.getElementById(`ui-${t}-add-btn`);
      if (prompt) prompt.style.display = 'none';
      if (addBtn) addBtn.style.display = 'flex';
    });
  }

  _deleteMapping(type, id) {
    const key      = type === 'cc' ? 'midiCCMappings' : 'midiNoteMappings';
    const mappings = { ...(this.state.get(key) || {}) };
    const prevName = mappings[id]?.name != null && String(mappings[id].name).trim();
    delete mappings[id];
    this.state.set(key, mappings);
    if (prevName) this._stripControlValue(prevName);
    this._pruneSceneBindings();
    this._renderSceneBindings();
  }

  _updateMappingField(type, id, patch) {
    const key = type === 'cc' ? 'midiCCMappings' : 'midiNoteMappings';
    const mappings = { ...(this.state.get(key) || {}) };
    const cur = mappings[id];
    if (!cur) return;
    const prevName = cur.name != null && String(cur.name).trim();
    const next = { ...cur };
    if ('name' in patch) next.name = String(patch.name).trim();
    delete next.target;
    mappings[id] = next;
    this.state.set(key, mappings);
    const newName = next.name != null && String(next.name).trim();
    if (prevName && prevName !== newName) {
      this._stripControlValue(prevName);
      this._pruneSceneBindings();
    }
    this._renderSceneBindings();
  }

  // ─────────────────────────────────────────────────
  // Slider
  // ─────────────────────────────────────────────────

  _applyTuneParam(paramKey, value) {
    if (GLOBAL_ENV_KEYS.has(paramKey)) return;
    const activeKey = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const pr = perf?.presets?.[activeKey];
    if (!pr || !activeKey) return;
    const m = getSceneMeta(pr.scene);
    const merged = { ...(m?.defaultParams || {}), ...(pr.params || {}) };
    const nextParams = { ...merged, [paramKey]: value };
    this._patchActivePreset({ params: nextParams });
    this.app?.applyActivePresetParams?.();
  }

  _applyUnifiedControlValue(controlKey, value) {
    const activeKey = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const pr = perf?.presets?.[activeKey];
    if (!pr || !activeKey) return;
    const meta = getSceneMeta(pr.scene);
    if (!meta) return;
    const nextPreset = setUnifiedControlValue(meta, pr, controlKey, value);
    this._patchActivePreset(nextPreset);
    this.app?.applyActivePresetParams?.();
  }

  /** While true, `performance` state updates skip rebuilding the performance panel (control sliders). */
  _armTuneParamDragRelease() {
    this._tuneParamDragging = true;
    window.addEventListener('mouseup', this._endTuneParamDrag, { once: true });
    window.addEventListener('touchend', this._endTuneParamDrag, { once: true });
    window.addEventListener('touchcancel', this._endTuneParamDrag, { once: true });
    window.addEventListener('pointerup', this._endTuneParamDrag, { once: true });
    window.addEventListener('pointercancel', this._endTuneParamDrag, { once: true });
  }

  _bindPerfUnifiedControlSliders(controlsEl, meta, controlDefs, mergedParams) {
    for (const control of controlDefs) {
      const paramKey = control.legacy?.paramKey;
      if (!paramKey || (control.kind !== 'number' && control.kind !== 'color')) continue;

      const domBase = perfControlDomId(control.key);
      const track = controlsEl.querySelector(`#${domBase}-track`);
      const fill = controlsEl.querySelector(`#${domBase}-fill`);
      const thumb = controlsEl.querySelector(`#${domBase}-thumb`);
      const valEl = controlsEl.querySelector(`#${domBase}-val`);
      if (!track) continue;

      const defVal = meta?.defaultParams?.[paramKey];
      const legacySpec = defVal !== undefined ? resolveParamSpec(meta, paramKey, defVal) : null;

      if (control.kind === 'color' || legacySpec?.type === 'hue') {
        this._bindHueSlider(
          track,
          fill,
          thumb,
          valEl,
          String(mergedParams[paramKey] ?? defVal ?? '#66aaff'),
          (hex) => this._applyUnifiedControlValue(control.key, hex),
          true
        );
        continue;
      }

      const min = control.ui?.min ?? legacySpec?.min ?? 0;
      const max = control.ui?.max ?? legacySpec?.max ?? 1;
      const step = control.ui?.step ?? legacySpec?.step ?? null;
      const format =
        legacySpec?.format ||
        (max <= 1 && min >= 0
          ? (v) => v.toFixed(2)
          : (v) => String(Math.round(v)));

      this._bindSliderTrack(
        track,
        fill,
        thumb,
        valEl,
        min,
        max,
        Number(mergedParams[paramKey] ?? defVal ?? min),
        step,
        (nv) => this._applyUnifiedControlValue(control.key, nv),
        format,
        true
      );
    }
  }

  /**
   * @param {HTMLElement | null} track
   * @param {HTMLElement | null} fill
   * @param {HTMLElement | null} thumb
   * @param {HTMLElement | null} valEl
   * @param {number | null} step
   * @param {(v: number) => string} [format]
   * @param {boolean} [tuneDrag] — defer perf panel re-render until pointer release (Controls sliders)
   */
  _bindSliderTrack(track, fill, thumb, valEl, min, max, initial, step, onChange, format, tuneDrag = false) {
    if (!track) return;
    const span = max - min;
    const clampPct = (pct) => Math.max(0, Math.min(1, pct));
    const snap = (v) => {
      if (step == null || step <= 0) return v;
      const n = Math.round((v - min) / step) * step + min;
      return Math.max(min, Math.min(max, Number.isInteger(step) ? Math.round(n) : n));
    };
    const setFromPct = (pct, silent) => {
      pct = clampPct(pct);
      let v = span === 0 ? min : min + span * pct;
      v = snap(v);
      const adjPct = span === 0 ? 0 : (v - min) / span;
      if (fill) fill.style.width = `${adjPct * 100}%`;
      if (thumb) thumb.style.left = `${adjPct * 100}%`;
      if (valEl) valEl.textContent = format ? format(v) : v.toFixed(2);
      if (!silent) onChange(v);
    };
    setFromPct(span === 0 ? 0 : (snap(initial) - min) / span, true);

    let dragging = false;
    const update = (clientX) => {
      const rect = track.getBoundingClientRect();
      const w = rect.width;
      setFromPct(w <= 0 ? 0 : (clientX - rect.left) / w, false);
    };

    track.addEventListener('mousedown', (e) => {
      if (tuneDrag) this._armTuneParamDragRelease();
      dragging = true;
      update(e.clientX);
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (dragging) update(e.clientX);
    });
    window.addEventListener('mouseup', () => {
      dragging = false;
    });
    track.addEventListener(
      'touchstart',
      (e) => {
        if (tuneDrag) this._armTuneParamDragRelease();
        dragging = true;
        if (e.touches[0]) update(e.touches[0].clientX);
        e.preventDefault();
      },
      { passive: false }
    );
    window.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging || !e.touches[0]) return;
        update(e.touches[0].clientX);
      },
      { passive: false }
    );
    window.addEventListener('touchend', () => {
      dragging = false;
    });
  }

  _bindHueSlider(track, fill, thumb, valEl, hex, onChange, tuneDrag = false) {
    let h;
    let s;
    let l;
    try {
      [h, s, l] = hexToHsl(hex);
    } catch {
      h = 210;
      s = 0.5;
      l = 0.55;
    }
    const setFromPct = (pct, silent) => {
      pct = Math.max(0, Math.min(1, pct));
      const nh = pct * 360;
      const nhex = hslToHex(nh, s, l);
      if (fill) fill.style.width = `${pct * 100}%`;
      if (thumb) thumb.style.left = `${pct * 100}%`;
      if (valEl) valEl.textContent = nhex;
      if (!silent) onChange(nhex);
    };
    setFromPct(h / 360, true);

    let dragging = false;
    const update = (clientX) => {
      const rect = track.getBoundingClientRect();
      const w = rect.width;
      setFromPct(w <= 0 ? 0 : (clientX - rect.left) / w, false);
    };

    track.addEventListener('mousedown', (e) => {
      if (tuneDrag) this._armTuneParamDragRelease();
      dragging = true;
      update(e.clientX);
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (dragging) update(e.clientX);
    });
    window.addEventListener('mouseup', () => {
      dragging = false;
    });
    track.addEventListener(
      'touchstart',
      (e) => {
        if (tuneDrag) this._armTuneParamDragRelease();
        dragging = true;
        if (e.touches[0]) update(e.touches[0].clientX);
        e.preventDefault();
      },
      { passive: false }
    );
    window.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging || !e.touches[0]) return;
        update(e.touches[0].clientX);
      },
      { passive: false }
    );
    window.addEventListener('touchend', () => {
      dragging = false;
    });
  }

  _setupSlider(trackId, fillId, thumbId, valId, min, max, initial, onChange, format) {
    const track = document.getElementById(trackId);
    const fill = document.getElementById(fillId);
    const thumb = document.getElementById(thumbId);
    const valEl = document.getElementById(valId);
    this._bindSliderTrack(track, fill, thumb, valEl, min, max, initial, null, onChange, format);
  }

  // ─────────────────────────────────────────────────
  // Utils
  // ─────────────────────────────────────────────────

  _on(id, event, handler) {
    document.getElementById(id)?.addEventListener(event, handler);
  }

  destroy() {
    clearInterval(this._msgRateInterval);
    this._cancelLearn();
    this._endTuneParamDrag();
  }
}
